/**
 * OAuth helpers — redirect to provider start, parse callback fragment.
 */

import { accountApi } from './api'
import { APP } from './constants'

export type OAuthProvider = 'google' | 'microsoft'

/** Redirect the browser to the backend's /oauth/{provider}/start.
 *
 * Para login, registro persona, o registro empresa SIN datos pre-validados.
 * Para registro empresa CON datos pre-validados usa `prepareOAuth`. */
export function startOAuth(provider: OAuthProvider, intent: 'login' | 'register' = 'login'): void {
  const url = `${APP.accountUrl}/api/v1/auth/oauth/${provider}/start?intent=${intent}`
  window.location.href = url
}

/** Para registro empresa: el frontend ya validó la empresa (NIT lookup +
 * confirmación). Aquí mandamos esos datos al backend para que los guarde
 * en el state Redis. Cuando completen el callback OAuth, el backend crea
 * la org + membership automáticamente. */
export async function prepareOAuth(
  provider: OAuthProvider,
  intent: 'login' | 'register',
  pendingOrgData: Record<string, unknown> | null,
): Promise<void> {
  const r = await accountApi.post<{ redirect_url: string; expires_in: number }>(
    `/api/v1/auth/oauth/${provider}/prepare`,
    { intent, pending_org_data: pendingOrgData },
  )
  window.location.href = r.data.redirect_url
}

/** Parse the URL fragment populated by /oauth/{provider}/callback. */
export interface CallbackPayload {
  // Success path
  access_token?: string
  refresh_token?: string
  expires_in?: string
  person_id?: string
  active_org_id?: string
  // MFA required path
  mfa_required?: boolean
  mfa_challenge_token?: string
  mfa_challenge_expires_in?: string
  email?: string
  // Error path
  error?: string
}

export function parseCallbackFragment(fragment: string): CallbackPayload {
  const cleaned = fragment.startsWith('#') ? fragment.slice(1) : fragment
  if (!cleaned) return {}
  const params = new URLSearchParams(cleaned)
  return {
    access_token: params.get('access_token') ?? undefined,
    refresh_token: params.get('refresh_token') ?? undefined,
    expires_in: params.get('expires_in') ?? undefined,
    person_id: params.get('person_id') ?? undefined,
    active_org_id: params.get('active_org_id') ?? undefined,
    mfa_required: params.get('mfa_required') === '1',
    mfa_challenge_token: params.get('mfa_challenge_token') ?? undefined,
    mfa_challenge_expires_in: params.get('mfa_challenge_expires_in') ?? undefined,
    email: params.get('email') ?? undefined,
    error: params.get('error') ?? undefined,
  }
}
